import PropTypes from 'prop-types';
import { parse } from 'query-string';
import { Redirect, Route } from 'react-router';
import { compose, setPropTypes } from 'recompose';
import api from '../../../../api';
import * as ROUTES from '../../../../routes';
import Switch from '../../../../routing/Switch';
import AlertBox from '../../../components/AlertBox';
import AppProvider from '../../../components/AppProvider';
import LoadingIndicatorFull from '../../../components/LoadingIndicatorFull';
import NavigationTabs from '../../../components/NavigationTabs';
import PageHeader from '../../../components/PageHeader';
import PageStatic from '../../../components/PageStatic';
import PageStaticSection from '../../../components/PageStaticSection';
import PageTitle from '../../../components/PageTitle';
import { mapProps } from '../../../hoc/recompose';
import { withData } from '../../../hoc/withData';
import CompanyLink from '../UserImport/CompanyLink';
import HistoryTab from '../UserImport/History';
import PreviewTab from '../UserImport/Preview';
import SyncedByOtherSystemWarning from '../UserImport/SyncedByOtherSystemWarning';
import IntegrationsBreadcrumbs from '../components/IntegrationsBreadcrumbs';
import ConfigTab from './Configuration';

export default compose(
    setPropTypes({
        location: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
    }),
    mapProps(props => ({ companyId: parse(props.location.search).company, ...props })),
    withData({
        observe: {
            config: ({ companyId }) => api.workableImportConfiguration.getConfig.createObservable(companyId),
        },
        pendingComponent: () => <LoadingIndicatorFull />,
    })
)(AdminWorkablePage);

const hasConnectionDetails = config => {
    return !!(
        config.systemSpecificProperties.workableSubdomain && config.systemSpecificProperties.workablePartnerToken
    );
};

AdminWorkablePage.propTypes = {
    config: PropTypes.object.isRequired,
};

function AdminWorkablePage({ config, companyId, location: { search }, history }) {
    const withSearch = pathname => ({ pathname, search });

    const tabs = [
        {
            label: 'Configuration',
            link: withSearch(ROUTES.INTEGRATIONS_WORKABLE_CONFIG),
            visible: true,
        },
        {
            label: 'History',
            link: withSearch(ROUTES.INTEGRATIONS_WORKABLE_HISTORY),
            visible: true,
        },
        {
            label: 'Preview',
            link: withSearch(ROUTES.INTEGRATIONS_WORKABLE_PREVIEW),
            visible: true,
            enabled: hasConnectionDetails(config),
        },
    ];

    return (
        <AppProvider>
            <PageTitle title="Workable Integration" />
            <PageStatic>
                <IntegrationsBreadcrumbs page="Workable" />
                <Header companyId={companyId} />
                <p className="page_static-section">
                    Small Improvements integrates with Workable so you can manage your employee data in one place and
                    import it directly into your Small Improvements account.
                    {/* TODO: Re-add help center link once it’s ready.
                    Learn more in the{' '}
                    <Link
                        href="..."
                        openInNewTab={true}
                    >
                        documentation
                    </Link>
                    . */}
                </p>
                <SyncedByOtherSystemWarning config={config} />
            </PageStatic>
            <NavigationTabs tabs={tabs} />
            <PageStaticSection>
                <Switch>
                    <Route
                        exact
                        path={ROUTES.INTEGRATIONS_WORKABLE_HISTORY}
                        render={() => (
                            <HistoryTab apiImport={api.workableImport} name="Workable" companyId={companyId} />
                        )}
                    />
                    <Route
                        exact
                        path={ROUTES.INTEGRATIONS_WORKABLE_PREVIEW}
                        render={() =>
                            hasConnectionDetails(config) ? (
                                <PreviewTab
                                    apiImport={api.workableImport}
                                    name="Workable"
                                    companyId={companyId}
                                    goToHistory={() => history.push(ROUTES.INTEGRATIONS_WORKABLE_HISTORY)}
                                />
                            ) : (
                                <AlertBox colour="YELLOW">
                                    Please enter your details in the Configuration tab in order to view a preview.
                                </AlertBox>
                            )
                        }
                    />
                    <Route
                        exact
                        path={ROUTES.INTEGRATIONS_WORKABLE_CONFIG}
                        render={() => (
                            <ConfigTab
                                config={config}
                                form="workableConfig"
                                disabled={!!companyId}
                                apiImportConfig={api.workableImportConfiguration}
                                hasConnectionDetails={hasConnectionDetails(config)}
                            />
                        )}
                    />
                    <Redirect
                        to={
                            hasConnectionDetails(config) && config.synchronizationInterval !== 'NEVER'
                                ? ROUTES.INTEGRATIONS_WORKABLE_HISTORY
                                : ROUTES.INTEGRATIONS_WORKABLE_CONFIG
                        }
                    />
                </Switch>
            </PageStaticSection>
        </AppProvider>
    );
}

function Header({ companyId }) {
    const title = 'Workable Integration';

    if (!companyId) {
        return <PageHeader title={title} />;
    }

    return (
        <PageHeader
            title={
                <span>
                    {title} for <CompanyLink companyId={companyId} />
                </span>
            }
        />
    );
}
